/**
 * Event Appointment Updates — Report 1 (requirements R1-01 … R1-12).
 *
 * Access: <RequireEventViewer> (App.tsx); the API and Supabase RLS each
 * enforce it again. One fetch on open loads everything since October 2023;
 * every filter is client-side over that dataset (R1-11). The date filter keys
 * on the appointment date in Central time, falling back to the entry date for
 * leads that never got an appointment (same rule as the API).
 */
import { AlertCircle, ArrowDown, ArrowUp, Download, FileSpreadsheet } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { EventsNav } from "../../events/EventsNav";
import { DefinitionsPanel } from "../../events/DefinitionsPanel";
import { REPORT1_DEFINITIONS } from "../../events/definitions";
import { downloadXlsx } from "../../events/xlsx";
import { MultiSelect } from "../../events/MultiSelect";
import { downloadCsv } from "../../events/csv";
import { addDays, apptLabel, money, pct, reportDate, stampLabel, todayYmd } from "../../events/format";
import { fetchEventAppointments } from "../../lib/eventReporting";
import type { EventAppointment, EventAppointmentsResponse } from "../../lib/eventReporting";

const NO_REP = "(No rep)";
const HISTORY_FROM = "2023-10-01"; // ASHI asked for event history back to October 2023
const AHEAD_DAYS = 60; // leads can have appointments booked after today

type SortKey = "name" | "event" | "appt" | "product" | "result" | "price" | "rep" | "commission" | "mgr" | "demo" | "net";

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: "name", label: "Prospect Name" },
  { key: "event", label: "Event" },
  { key: "appt", label: "Appointment Date" },
  { key: "product", label: "Interested In" },
  { key: "result", label: "Result" },
  { key: "price", label: "Sale Price", numeric: true },
  { key: "rep", label: "Event Rep" },
  { key: "commission", label: "Commission", numeric: true },
  { key: "mgr", label: "Manager Override", numeric: true },
  { key: "demo", label: "Demo Pay", numeric: true },
  { key: "net", label: "Net Revenue", numeric: true },
];

/** Sale Price is the original sale amount, shown only for leads that sold. */
const salePrice = (r: EventAppointment) => (r.sale_flag ? r.gsa : null);
const rep = (r: EventAppointment) => r.promoter_name || NO_REP;
/** Event manager's override on this lead (override % on every event sale). Her own sales also earn
 *  the regular commission as promoter, so she gets 2% + 1% = 3% on those. */
const managerPay = (r: EventAppointment) => r.override;
const HOURS_FROM = "2024-12-01"; // LP Event Hours (with hours > 0) start in Dec 2024
const OVERRIDE_PREF = "events.showManagerOverride";

function readShowOverride(): boolean {
  try {
    return window.localStorage.getItem(OVERRIDE_PREF) !== "0"; // shown by default
  } catch {
    return true;
  }
}

function fmtHours(v: number): string {
  return v.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function sortValue(r: EventAppointment, key: SortKey): string | number {
  switch (key) {
    case "name": return (r.customer_name ?? "").toLowerCase();
    case "event": return r.subsource.toLowerCase();
    case "appt": return r.apptdate ?? r.entrydate ?? "";
    case "product": return (r.product ?? "").toLowerCase();
    case "result": return (r.display_label ?? "").toLowerCase();
    case "price": return salePrice(r) ?? -Infinity;
    case "rep": return rep(r).toLowerCase();
    case "commission": return r.commission ?? -Infinity;
    case "mgr": return managerPay(r) ?? -Infinity;
    case "demo": return r.demo_pay ?? -Infinity;
    case "net": return r.net_sale_amount ?? -Infinity;
  }
}

const uniq = (xs: (string | null)[]) => [...new Set(xs.filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b));
const sum = (xs: (number | null)[]) => xs.reduce<number>((a, b) => a + (b ?? 0), 0);

export function EventAppointments() {
  const today = todayYmd();
  const [data, setData] = useState<EventAppointmentsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [start, setStart] = useState(addDays(today, -30));
  const [end, setEnd] = useState(today);
  const [events, setEvents] = useState<string[]>([]);
  const [promoters, setPromoters] = useState<string[]>([]);
  const [products, setProducts] = useState<string[]>([]);
  const [results, setResults] = useState<string[]>([]);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "appt", dir: -1 });
  // Presentation mode: hide the manager's override when reviewing a rep's numbers with them.
  const [showOverride, setShowOverride] = useState(readShowOverride);
  function toggleOverride(next: boolean) {
    setShowOverride(next);
    try {
      window.localStorage.setItem(OVERRIDE_PREF, next ? "1" : "0");
    } catch {
      /* private window or blocked storage: the toggle still works for this visit */
    }
  }

  useEffect(() => {
    fetchEventAppointments(HISTORY_FROM, addDays(today, AHEAD_DAYS))
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, []); // load once on open (R1-11); `today` is fixed for the page's lifetime

  const all = data?.rows ?? [];
  const inDates = useMemo(
    () => all.filter((r) => { const d = reportDate(r); return !!d && d >= start && d <= end; }),
    [all, start, end],
  );
  const options = useMemo(() => ({
    // plus events that only have Register to Win registrations in the range (e.g. Windsurge 2025)
    events: uniq([...inDates.map((r) => r.subsource),
      ...(data?.rtw ?? []).filter((r) => r.entry_date >= start && r.entry_date <= end).map((r) => r.parent_subsource)]),
    promoters: uniq(inDates.map(rep)),
    products: uniq(inDates.map((r) => r.product)),
    results: uniq(inDates.map((r) => r.display_label)),
  }), [inDates, data, start, end]);

  const rows = useMemo(() => inDates.filter((r) =>
    (events.length === 0 || events.includes(r.subsource)) &&
    (promoters.length === 0 || promoters.includes(rep(r))) &&
    (products.length === 0 || products.includes(r.product ?? "")) &&
    (results.length === 0 || results.includes(r.display_label ?? "")),
  ), [inDates, events, promoters, products, results]);

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
    return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
  }), [rows, sort]);

  const commissionSet = data?.rates?.commission_pct != null;
  const totals = useMemo(() => {
    const sales = rows.filter((r) => r.net_sale_flag).length;
    const revenue = sum(rows.map((r) => r.net_sale_amount));
    return {
      commission: sum(rows.map((r) => r.commission)),
      managerPay: sum(rows.map(managerPay)),
      demos: rows.filter((r) => r.demo_flag).length,
      demoPay: sum(rows.map((r) => r.demo_pay)),
      revenue,
      sales,
      avgSale: sales ? revenue / sales : null,
    };
  }, [rows]);

  const funnel = useMemo(() => [
    { label: "Leads", n: rows.length },
    { label: "Set", n: rows.filter((r) => r.set_flag).length },
    { label: "Issued", n: rows.filter((r) => r.issue_flag).length },
    { label: "Needs Assessment", n: rows.filter((r) => r.na_flag).length },
    { label: "Demo", n: rows.filter((r) => r.demo_flag).length },
    { label: "Sale", n: rows.filter((r) => r.net_sale_flag).length },
  ], [rows]);

  const byRep = useMemo(() => {
    const m = new Map<string, RepStats>();
    const entry = (k: string) => {
      const e = m.get(k) ?? { leads: 0, demos: 0, sales: 0, commission: 0, managerPay: 0, demoPay: 0 };
      m.set(k, e);
      return e;
    };
    for (const r of rows) {
      const e = entry(rep(r));
      e.leads += 1;
      e.demos += r.demo_flag ? 1 : 0;
      e.sales += r.net_sale_flag ? 1 : 0;
      e.commission += r.commission ?? 0;
      e.demoPay += r.demo_pay ?? 0;
      // manager pay goes to whoever was manager on THIS lead's date (the role can change hands)
      if (r.manager_name) entry(r.manager_name).managerPay += managerPay(r) ?? 0;
    }
    const total = (v: RepStats) => v.commission + v.managerPay + v.demoPay;
    return [...m.entries()].sort((a, b) => total(b[1]) - total(a[1]) || a[0].localeCompare(b[0]));
  }, [rows]);

  // Hours Worked follows the date, Event and Event Rep filters only: LP Event Hours aren't recorded
  // per product or result, so the Interested In / Result filters don't apply to them.
  const hoursWorked = useMemo(() => (data?.hours ?? [])
    .filter((h) => h.work_date >= start && h.work_date <= end &&
      (events.length === 0 || events.includes(h.subsource)) &&
      (promoters.length === 0 || (h.promoter_name != null && promoters.includes(h.promoter_name))))
    .reduce((a, h) => a + h.hours, 0), [data, start, end, events, promoters]);
  const hoursNote = products.length + results.length > 0
    ? "Not filtered by Interested In or Result"
    : start < HOURS_FROM ? "LP Event Hours start Dec 2024" : undefined;

  // Register to Win (Renee, 2026-10-21): registrations by entry date, under the date and Event
  // filters. RTW leads have no rep, product or result, so the two RTW tiles show 0 whenever the
  // Event Rep, Interested In or Result filter is set.
  const rtwBlocked = promoters.length + products.length + results.length > 0;
  const rtwCount = useMemo(() => rtwBlocked ? 0 : (data?.rtw ?? [])
    .filter((r) => r.entry_date >= start && r.entry_date <= end &&
      (events.length === 0 || events.includes(r.parent_subsource)))
    .reduce((a, r) => a + r.leads, 0), [data, start, end, events, rtwBlocked]);
  const rtwNote = rtwBlocked ? "0 when Event Rep, Interested In or Result is filtered" : undefined;

  const columns = showOverride ? COLUMNS : COLUMNS.filter((c) => c.key !== "mgr");
  const managerNow = data?.rates?.manager_promoter_name ?? null;
  const filtersActive = events.length + promoters.length + products.length + results.length > 0;

  function exportRows() {
    const header = [...columns.map((c) => c.label), "Lead ID"];
    const body = sorted.map((r) => [
      r.customer_name, r.subsource, apptLabel(r.apptdate), r.product, r.display_label, salePrice(r),
      rep(r), r.commission, ...(showOverride ? [managerPay(r)] : []), r.demo_pay, r.net_sale_amount, r.lead_id,
    ]);
    return { header, body };
  }

  function exportCsv() {
    const { header, body } = exportRows();
    downloadCsv(`event-appointments_${start}_to_${end}.csv`, header, body);
  }

  function exportXlsx() {
    const { header, body } = exportRows();
    const money = header.flatMap((h, i) => (["Sale Price", "Commission", "Manager Override", "Demo Pay", "Net Revenue"].includes(h) ? [i] : []));
    void downloadXlsx(`event-appointments_${start}_to_${end}.xlsx`, [{
      sheet: "Event Appointments",
      title: ["Event Appointment Updates", `${start} to ${end} · refreshed ${stampLabel(data?.refreshed_at ?? null)}`],
      header, rows: body, money,
    }]);
  }

  function header(c: { key: SortKey; label: string; numeric?: boolean }) {
    const active = sort.key === c.key;
    return (
      <th key={c.key} className={`px-3 py-3 font-normal whitespace-nowrap ${c.numeric ? "text-right" : ""}`}
        aria-sort={active ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
        <button type="button" className="inline-flex items-center gap-1 uppercase tracking-[0.14em] focus-ring rounded-sm"
          onClick={() => setSort({ key: c.key, dir: active ? (sort.dir === 1 ? -1 : 1) : c.numeric ? -1 : 1 })}>
          {c.label}
          {active && (sort.dir === 1 ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
        </button>
      </th>
    );
  }

  return (
    <div className="p-8 view-in overflow-y-auto h-full scroll-quiet">
      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-[28px] leading-tight" style={{ color: "var(--ink)" }}>
            Event Appointment Updates
          </h1>
          <p className="text-[14.5px] font-serif italic mt-1" style={{ color: "var(--ink-3)" }}>
            Event leads, results and rep compensation · refreshed {stampLabel(data?.refreshed_at ?? null)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportCsv} disabled={!data || rows.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border hairline font-serif text-[14px] t-base focus-ring disabled:opacity-40"
            style={{ color: "var(--ink)" }}>
            <Download size={14} strokeWidth={2} />
            CSV
          </button>
          <button type="button" onClick={exportXlsx} disabled={!data || rows.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-serif text-[14px] t-base focus-ring disabled:opacity-40"
            style={{ background: "var(--ink)", color: "var(--bg)" }}>
            <FileSpreadsheet size={14} strokeWidth={2} />
            Export Excel
          </button>
        </div>
      </header>

      <EventsNav />

      {error && (
        <div className="rounded-md p-4 mb-4 font-mono text-[13px] flex items-start gap-2"
          style={{ background: "var(--oxblood-bg)", color: "var(--oxblood)", border: "1px solid var(--oxblood)" }}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>Failed to load the report: {error}</span>
        </div>
      )}

      {/* Filters: one row above everything they drive */}
      <section className="flex flex-wrap items-end gap-3 mb-5">
        <label className="block">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-3)" }}>From</div>
          <input type="date" value={start} min={HISTORY_FROM} max={end} onChange={(e) => e.target.value && setStart(e.target.value)}
            className="px-3 py-1.5 rounded-md border hairline text-[13.5px] focus-ring" style={{ background: "var(--bg-elev)", color: "var(--ink)" }} />
        </label>
        <label className="block">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-3)" }}>To</div>
          <input type="date" value={end} min={start} max={addDays(today, AHEAD_DAYS)} onChange={(e) => e.target.value && setEnd(e.target.value)}
            className="px-3 py-1.5 rounded-md border hairline text-[13.5px] focus-ring" style={{ background: "var(--bg-elev)", color: "var(--ink)" }} />
        </label>
        <MultiSelect label="Event" options={options.events} selected={events} onChange={setEvents} />
        <MultiSelect label="Event Rep" options={options.promoters} selected={promoters} onChange={setPromoters} />
        <MultiSelect label="Interested In" options={options.products} selected={products} onChange={setProducts} />
        <MultiSelect label="Result" options={options.results} selected={results} onChange={setResults} />
        {filtersActive && (
          <button type="button" onClick={() => { setEvents([]); setPromoters([]); setProducts([]); setResults([]); }}
            className="px-2 py-1.5 text-[12.5px] underline focus-ring rounded-sm" style={{ color: "var(--ink-3)" }}>
            Clear filters
          </button>
        )}
        <label className="ml-auto inline-flex items-center gap-2 py-1.5 text-[12.5px]" style={{ color: "var(--ink-3)" }}
          title="Hide the manager's override when reviewing a rep's numbers with them">
          <input type="checkbox" checked={showOverride} onChange={(e) => toggleOverride(e.target.checked)} />
          Show manager override
        </label>
      </section>

      {/* Activity tiles (Renee, 2026-10-21): above the pay tiles */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        <Tile label="Hours Worked" value={fmtHours(hoursWorked)} note={hoursNote} />
        <Tile label="Demos" value={totals.demos.toLocaleString("en-US")} />
        <Tile label="Register to Win" value={rtwCount.toLocaleString("en-US")} note={rtwNote} />
        <Tile label="Hours per Demo" value={totals.demos ? fmtHours(hoursWorked / totals.demos) : "–"} />
        <Tile label="Hours per Register to Win" value={rtwBlocked ? "0" : rtwCount ? fmtHours(hoursWorked / rtwCount) : "–"}
          note={rtwNote} />
      </section>

      {/* Summary tiles (R1-05) */}
      <section className={`grid grid-cols-2 ${showOverride ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 mb-5`}>
        <Tile label="Total Commission" value={commissionSet ? money(totals.commission) : "Not set"}
          note={commissionSet ? undefined : "Commission rate not entered yet"} />
        {showOverride && (
          <Tile label="Manager Override" value={money(totals.managerPay)}
            note={managerNow ? `${managerNow}: override on all event sales` : "No event manager set"} />
        )}
        <Tile label="Total Demo Pay" value={money(totals.demoPay)} />
        <Tile label="Total Revenue" value={money(totals.revenue)} note="Net of change orders and cancellations" />
        <Tile label="Average Sale" value={totals.avgSale == null ? "–" : money(totals.avgSale)}
          note={`${totals.sales} sale${totals.sales === 1 ? "" : "s"}`} />
      </section>

      <section className="grid lg:grid-cols-2 gap-3 mb-5">
        <Funnel steps={funnel} />
        <RepTable
          // with the override hidden, drop rows that exist only to carry the manager's override
          rows={showOverride ? byRep : byRep.filter(([, v]) => v.leads > 0)}
          commissionSet={commissionSet} showOverride={showOverride} />
      </section>

      <DefinitionsPanel
        items={REPORT1_DEFINITIONS}
        summary={`Commission ${pct(data?.rates?.commission_pct)} · Demo pay ${data?.rates?.demo_pay_rate == null ? "Not set" : money(data.rates.demo_pay_rate)}`}
      />

      <section className="rounded-lg border hairline overflow-hidden" style={{ background: "var(--bg-elev)" }}>
        <div className="flex items-center justify-between px-5 py-3 border-b hairline">
          <span className="font-serif text-[14px]" style={{ color: "var(--ink)" }}>
            {rows.length} lead{rows.length === 1 ? "" : "s"}
          </span>
        </div>
        {!data && !error ? (
          <div className="p-8 font-serif italic text-[14px]" style={{ color: "var(--ink-3)" }}>Loading report…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 font-serif italic text-[14px]" style={{ color: "var(--ink-3)" }}>
            No event leads match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto scroll-quiet">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="text-[10.5px] font-mono border-b hairline" style={{ color: "var(--ink-3)" }}>
                  {columns.map(header)}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.lead_id} className="border-b hairline last:border-0" style={{ color: "var(--ink)" }}>
                    <td className="px-3 py-2">{r.customer_name}</td>
                    <td className="px-3 py-2">{r.subsource}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{apptLabel(r.apptdate) || <Muted>No appointment</Muted>}</td>
                    <td className="px-3 py-2">{r.product}</td>
                    <td className="px-3 py-2">{r.display_label}</td>
                    <td className="px-3 py-2 text-right font-mono">{money(salePrice(r))}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.promoter_name || <Muted>{NO_REP}</Muted>}</td>
                    <td className="px-3 py-2 text-right font-mono">{money(r.commission)}</td>
                    {showOverride && <td className="px-3 py-2 text-right font-mono">{money(managerPay(r))}</td>}
                    <td className="px-3 py-2 text-right font-mono">{r.demo_pay ? money(r.demo_pay) : ""}</td>
                    <td className="px-3 py-2 text-right font-mono">{money(r.net_sale_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <span style={{ color: "var(--ink-4)" }}>{children}</span>;
}

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rounded-lg border hairline px-5 py-4" style={{ background: "var(--bg-elev)" }}>
      <div className="text-[10.5px] font-mono uppercase tracking-[0.14em]" style={{ color: "var(--ink-3)" }}>{label}</div>
      <div className="font-serif text-[26px] leading-tight mt-1" style={{ color: "var(--ink)" }}>{value}</div>
      {note && <div className="text-[12px] mt-0.5" style={{ color: "var(--ink-3)" }}>{note}</div>}
    </div>
  );
}

/** Fall-off funnel: one series, one hue; count and % of leads labelled on every step (6 bars). */
function Funnel({ steps }: { steps: { label: string; n: number }[] }) {
  const top = steps[0]?.n || 0;
  return (
    <div className="rounded-lg border hairline px-5 py-4" style={{ background: "var(--bg-elev)" }}>
      <div className="font-serif text-[14px] mb-3" style={{ color: "var(--ink)" }}>Fall-off funnel</div>
      <ol className="grid gap-2" aria-label="Leads reaching each stage">
        {steps.map((s) => {
          const share = top ? s.n / top : 0;
          const tip = `${s.label}: ${s.n} lead${s.n === 1 ? "" : "s"} (${Math.round(share * 100)}% of leads)`;
          return (
            <li key={s.label} className="grid grid-cols-[120px_1fr_88px] items-center gap-3 text-[13px] group" title={tip}>
              <span style={{ color: "var(--ink-2)" }}>{s.label}</span>
              <span className="h-[14px] rounded-r-[4px] block" style={{ background: "var(--line-2)" }}>
                <span className="h-full rounded-r-[4px] block t-base group-hover:opacity-80"
                  style={{ width: `${Math.max(share * 100, s.n ? 0.8 : 0)}%`, background: "var(--forest-soft)" }} />
              </span>
              <span className="text-right font-mono" style={{ color: "var(--ink)" }}>
                {s.n} <span style={{ color: "var(--ink-3)" }}>{top ? `${Math.round(share * 100)}%` : ""}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

interface RepStats { leads: number; demos: number; sales: number; commission: number; managerPay: number; demoPay: number }

function RepTable({ rows, commissionSet, showOverride }: {
  rows: [string, RepStats][]; commissionSet: boolean; showOverride: boolean;
}) {
  return (
    <div className="rounded-lg border hairline overflow-hidden" style={{ background: "var(--bg-elev)" }}>
      <div className="font-serif text-[14px] px-5 pt-4 pb-2" style={{ color: "var(--ink)" }}>Compensation by rep</div>
      <div className="max-h-[260px] overflow-y-auto scroll-quiet">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="text-[10.5px] font-mono uppercase tracking-[0.14em] border-b hairline whitespace-nowrap" style={{ color: "var(--ink-3)" }}>
              <th className="px-5 py-2 font-normal">Event Rep</th>
              <th className="px-2 py-2 font-normal text-right">Leads</th>
              <th className="px-2 py-2 font-normal text-right">Demos</th>
              <th className="px-2 py-2 font-normal text-right">Sales</th>
              <th className="px-2 py-2 font-normal text-right">Commission</th>
              {showOverride && <th className="px-2 py-2 font-normal text-right">Mgr Override</th>}
              <th className="px-2 py-2 font-normal text-right">Demo Pay</th>
              <th className="px-5 py-2 font-normal text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([name, v]) => (
              <tr key={name} className="border-b hairline last:border-0" style={{ color: "var(--ink)" }}>
                <td className="px-5 py-1.5 whitespace-nowrap">{name}</td>
                <td className="px-2 py-1.5 text-right font-mono">{v.leads}</td>
                <td className="px-2 py-1.5 text-right font-mono">{v.demos}</td>
                <td className="px-2 py-1.5 text-right font-mono">{v.sales}</td>
                <td className="px-2 py-1.5 text-right font-mono">{commissionSet ? money(v.commission) : "–"}</td>
                {showOverride && <td className="px-2 py-1.5 text-right font-mono">{v.managerPay ? money(v.managerPay) : ""}</td>}
                <td className="px-2 py-1.5 text-right font-mono">{money(v.demoPay)}</td>
                <td className="px-5 py-1.5 text-right font-mono">{money(v.commission + (showOverride ? v.managerPay : 0) + v.demoPay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
